import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { db } from "../../lib/firebase";
import { doc, getDoc, updateDoc, deleteField } from "firebase/firestore";
import { sendDepositNotificationEmail } from "../../lib/depositEmail";
import "../../styles/Deposit.css";

const Deposit = ({ loading, handleCloseModal, user }) => {
  const [copied, setCopied] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ amount: "", accountType: "" });
  const [wallets, setWallets] = useState({});
  const [pendingPayment, setPendingPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const checkPendingPayment = async () => {
      if (user?.uid) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.pendingPayment) {
            setPendingPayment(data.pendingPayment);
            setStep(3);
          }
        }
      }
    };
    checkPendingPayment();
  }, [user]);

  const handleSavePayment = async () => {
    if (!user?.uid || submitting) return;
    setSubmitting(true);
    try {
      const selectedEntry = Object.values(wallets).find(
        (w) => w.name === form.accountType,
      );
      const paymentData = {
        amount: Number(form.amount),
        accountType: form.accountType,
        walletAddress: selectedEntry?.address || "",
        date: new Date().toISOString(),
        status: "pending",
        type: "deposit",
      };

      await updateDoc(doc(db, "users", user.uid), {
        pendingPayment: paymentData,
      });

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      await sendDepositNotificationEmail(userData, paymentData);

      setPendingPayment(paymentData);
      setStep(3);
    } catch (err) {
      console.error("Error saving payment:", err);
    } finally {
      setSubmitting(false);
    }
  };
  const handleCancelPayment = async () => {
    if (!user?.uid) return;
    setCanceling(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        pendingPayment: deleteField(),
      });
      setPendingPayment(null);
      setStep(1);
    } catch (err) {
      console.error("Error canceling payment:", err);
    } finally {
      setCanceling(false);
    }
  };

  useEffect(() => {
    async function fetchWallets() {
      try {
        const snap = await getDoc(doc(db, "users", "wallets"));
        if (snap.exists()) {
          const data = snap.data();
          const sorted = Object.entries(data)
            .filter(([k]) => /^wallet\d+$/.test(k))
            .sort(
              (a, b) =>
                parseInt(a[0].replace("wallet", ""), 10) -
                parseInt(b[0].replace("wallet", ""), 10),
            )
            .reduce((obj, [k, w]) => {
              obj[k] = { name: w.name, address: w.address };
              return obj;
            }, {});
          setWallets(sorted);
          const first = Object.values(sorted)[0]?.name || "";
          setForm((prev) => ({ ...prev, accountType: first }));
        }
      } catch (err) {
        console.error("Error loading wallets:", err);
      }
    }
    fetchWallets();
  }, []);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    setStep(2);
  };

  if (loading) {
    return (
      <div className="deposit-page">
        <h2>Deposit Crypto to Start</h2>
        <p>Loading wallet addresses...</p>
      </div>
    );
  }

  const selectedEntry = Object.values(wallets).find(
    (w) => w.name === form.accountType,
  );
  const selectedAddress = selectedEntry?.address || "";

  const currentStep = pendingPayment ? 3 : step;

  const renderContent = () => {
    if (currentStep === 3 || pendingPayment) {
      const isBuy = pendingPayment.type === "buy";
      return (
        <div className="pending-confirmation">
          <h2>Waiting for {isBuy ? "Buy" : "Deposit"} Confirmation</h2>
          <p className="confirm-text">
            Your payment is being confirmed. Please wait while we verify your
            transaction.
          </p>
          <div className="pending-details">
            <div className="pending-row">
              <span>Transaction Type</span>
              <span style={{ textTransform: "capitalize" }}>{pendingPayment.type || "deposit"}</span>
            </div>
            <div className="pending-row">
              <span>Amount</span>
              <span>${pendingPayment.amount.toLocaleString()}</span>
            </div>
            <div className="pending-row">
              <span>Account Type</span>
              <span>{pendingPayment.accountType}</span>
            </div>
            <div className="pending-row">
              <span>Status</span>
              <span className="pending-status">{pendingPayment.status}</span>
            </div>
            <div className="pending-row">
              <span>Date</span>
              <span>{new Date(pendingPayment.date).toLocaleDateString()}</span>
            </div>
          </div>
          <button
            className="deposit-btn secondary"
            style={{ marginTop: "20px", width: "100%" }}
            onClick={handleCancelPayment}
            disabled={canceling}
          >
            {canceling ? "Deleting..." : isBuy ? "Cancel Payment" : "Cancel Deposit"}
          </button>
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <>
          <button className="go-back-link" onClick={handleCloseModal}>
            ← Go back to dashboard
          </button>

          <h2>Deposit Instructions</h2>

          <div className="crypto-address-box">
            <div className="qr-code-container">
              <QRCodeSVG value={selectedAddress} size={140} level="H" />
            </div>
            <div className="address-info">
              <h3>{form.accountType.toUpperCase()}</h3>

              <div className="deposit-preview">
                <span className="preview-text">Please send exactly</span>
                <span className="preview-amount">
                  ${Number(form.amount).toLocaleString()}
                </span>
                <span className="preview-text">worth of</span>
                <span className="preview-crypto">
                  {form.accountType.toUpperCase()}
                </span>
                <span className="preview-text">to the address below</span>
              </div>

              <div className="address-container">
                <button
                  className="copy-button"
                  onClick={() => handleCopy(selectedAddress)}
                  title="Copy"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <p className="address">{selectedAddress}</p>
              </div>
            </div>
          </div>

          <div className="deposit-buttons">
            <button
              className="deposit-btn secondary"
              onClick={() => setStep(1)}
            >
              Change Amount / Account
            </button>
            <button
              className="deposit-btn primary"
              onClick={handleSavePayment}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "I've paid!"}
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <h2>Enter Deposit Details</h2>
        <form onSubmit={handleSubmit}>
          <label>
            Amount:
            <input
              className="input-field"
              type="number"
              name="amount"
              placeholder="eg. 1000"
              value={form.amount}
              onChange={handleInputChange}
              min="0"
              step="any"
              required
            />
          </label>

          <label>
            Select Account:
            <select
              className="input-field"
              name="accountType"
              value={form.accountType}
              onChange={handleInputChange}
              required
            >
              {Object.values(wallets).map((w) => (
                <option key={w.name} value={w.name}>
                  {w.name.toUpperCase()}
                </option>
              ))}
            </select>
          </label>

          <button type="submit" className="delete-button">
            Continue
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="deposit-page">
      {copied && <div className="copied-notification">Copied!</div>}
      {renderContent()}
    </div>
  );
};

export default Deposit;
